import React, { FC, useState, useEffect } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { Image, Spinner } from 'ui'
import { Table, TableBody, TableCell, TableRow } from '@material-ui/core'
import ProtocolsEmpty from './ProtocolsEmpty'
import MekomeTablePagination from 'components/MekomeTablePagination'
import { cleanImportedText } from './textUtils'
import ProtocolsSearchBar from './ProtocolsSearchBar'
import ProtocolsFilterPanel, { ProtocolsFilters, emptyProtocolsFilters, filtersToListQuery } from './ProtocolsFilterPanel'
import ConfirmationDialog from './ConfirmationDialog'
import useStore from 'store'
import { getProtocolPermissions } from './permissions'
import { SearchAndFilterWrapper } from './Protocols.styles'

import {
  Header,
  HeaderTitle,
  BackButton,
  TableBar,
  BackHeader,
  HeaderTitleContainer,
  BackHeaderText,
  HeaderTitle1,
  HeaderIcon,
  TitleMain,
  TitleSecond,
  TableHeadStyled,
  TableContainerStyled,
  TableCellStyled,
  TableCellCentered,
  HeadCell,
  HeadCellCentered,
  TitleCell,
  CategoryBadge,
  DateCell,
  AuthorCell,
  VersionCell,
  RestoreButton,
  DeleteButton,
  ActionsCell,
  ActionImage,
} from './ArchiveList.styles'

import Icon4 from 'assets/icons/protocols/icon4.svg'
import Icon5 from 'assets/icons/protocols/icon5.svg'
import Icon6 from 'assets/icons/protocols/icon6.svg'
import RemoveIcon from 'assets/icons/protocols/icon7.svg'
import RestoreIcon from 'assets/icons/protocols/icon8.svg'
import FileIcon from 'assets/icons/ims/File.svg'

type Props = {
  onBack: () => void
}

// Same rendering as the protocols table, so a document shows one date format
// wherever it is listed.
const formatProtocolDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('he-IL') : ''

const ArchiveList: FC<Props> = ({ onBack }) => {
  const { t } = useTranslation('protocols')
  const { protocolsStore } = useStore()
  const [filterActive, setFilterActive] = useState(false)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<ProtocolsFilters>(emptyProtocolsFilters)
  // Free-text inputs are debounced so typing fires one request per pause.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [debouncedCreator, setDebouncedCreator] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [removeItemId, setRemoveItemId] = useState<number | null>(null)
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false)
  const [restoreItemId, setRestoreItemId] = useState<number | null>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 350)
    return () => clearTimeout(id)
  }, [search])
  useEffect(() => {
    const id = setTimeout(() => setDebouncedCreator(filters.creatorName), 350)
    return () => clearTimeout(id)
  }, [filters.creatorName])

  const archiveQuery = () => ({
    pageNumber: page + 1,
    pageSize: rowsPerPage,
    ...filtersToListQuery({ ...filters, creatorName: debouncedCreator }, debouncedSearch, { includeStatus: true }),
  })

  const reloadArchive = () => protocolsStore.loadArchive(archiveQuery())

  useEffect(() => {
    reloadArchive()
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    debouncedCreator,
    filters.mode,
    filters.status,
    filters.categoryId,
    filters.fromDate,
    filters.toDate,
  ])

  const { archiveItems, archiveTotalCount, loadingArchive } = protocolsStore
  const perms = getProtocolPermissions(protocolsStore.userPermissions)

  return (
    <>
      <ConfirmationDialog
        open={openConfirmDialog}
        closeModal={() => setOpenConfirmDialog(false)}
        onConfirm={async () => {
          await protocolsStore.deleteProtocol(removeItemId)
          setOpenConfirmDialog(false)
          reloadArchive()
        }}
        TitleMain={t`archive_delete_title`}
        TitleSecond={t`archive_delete_question`}
        confirmBtnColor="#fb2c36"
        ConfirmButtonText={t`archive_delete_confirm`}
        CancelButtonText={t`cancel_short`}
      />
      <ConfirmationDialog
        open={openRestoreDialog}
        closeModal={() => setOpenRestoreDialog(false)}
        onConfirm={async () => {
          await protocolsStore.restoreProtocol(restoreItemId)
          setOpenRestoreDialog(false)
          reloadArchive()
        }}
        TitleMain={t`archive_restore_title`}
        TitleSecond={t`archive_restore_question`}
        confirmBtnColor="#00c951"
        ConfirmButtonText={t`archive_restore_confirm`}
        CancelButtonText={t`cancel_short`}
      />
      <Header>
        <HeaderTitle>
          <HeaderTitleContainer>
            <HeaderIcon><Image src={Icon5} /></HeaderIcon>
            <HeaderTitle1>
              <TitleMain>{t`archive_title`}</TitleMain>
              <TitleSecond>{t('archive_subtitle', { count: archiveTotalCount })}</TitleSecond>
            </HeaderTitle1>
          </HeaderTitleContainer>
          <BackHeader>
            <BackHeaderText>{t`back_to_previous`}</BackHeaderText>
            <BackButton onClick={onBack}>
              <Image src={Icon4} />
            </BackButton>
          </BackHeader>
        </HeaderTitle>

        <SearchAndFilterWrapper>
          <ProtocolsSearchBar
            filterActive={filterActive}
            onFilterToggle={() => setFilterActive(!filterActive)}
            onSearch={val => { setPage(0); setSearch(val) }}
          />

          {filterActive && (
            <ProtocolsFilterPanel
              value={filters}
              searchText={search}
              onChange={patch => { setPage(0); setFilters(prev => ({ ...prev, ...patch })) }}
              onReset={() => { setPage(0); setFilters(emptyProtocolsFilters) }}
              authorInputId="archive-creator-name"
            />
          )}
        </SearchAndFilterWrapper>
      </Header>

      <TableBar>
        <TableContainerStyled>
          <Table stickyHeader>
            <TableHeadStyled>
              <TableRow>
                <HeadCell>{t`col_title`}</HeadCell>
                <HeadCellCentered>{t`col_category`}</HeadCellCentered>
                <HeadCell>{t`col_author`}</HeadCell>
                <HeadCell>{t`col_date`}</HeadCell>
                <HeadCellCentered>{t`col_version`}</HeadCellCentered>
                <HeadCell>{t`col_actions`}</HeadCell>
              </TableRow>
            </TableHeadStyled>
            <TableBody>
              {loadingArchive && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Spinner />
                  </TableCell>
                </TableRow>
              )}
              {!loadingArchive && archiveItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <ProtocolsEmpty title={t`archive_empty`} />
                  </TableCell>
                </TableRow>
              )}
              {!loadingArchive && archiveItems.map(item => (
                <TableRow key={item.id} hover>
                  <TableCellStyled>
                    <TitleCell>
                      <Image src={FileIcon} />
                      {cleanImportedText(item.title)}
                    </TitleCell>
                  </TableCellStyled>
                  <TableCellCentered>
                    <CategoryBadge>{cleanImportedText(item.categoryName)}</CategoryBadge>
                  </TableCellCentered>
                  <TableCellStyled>
                    <AuthorCell>{item.creatorName}</AuthorCell>
                  </TableCellStyled>
                  <TableCellStyled>
                    <DateCell>
                      <Image src={Icon6} />
                      {formatProtocolDate(item.protocolDate)}
                    </DateCell>
                  </TableCellStyled>
                  <TableCellCentered>
                    <VersionCell>{item.version}</VersionCell>
                  </TableCellCentered>
                  <TableCellStyled>
                    <ActionsCell>
                      {perms.canRestoreProtocol && (
                        <RestoreButton startIcon={<ActionImage src={RestoreIcon}/>} onClick={() => { setRestoreItemId(item.id); setOpenRestoreDialog(true) }}> {t`archive_restore_confirm`}</RestoreButton>
                      )}
                      {perms.canDeleteArchivedProtocol && (
                        <DeleteButton startIcon={<ActionImage src={RemoveIcon}/>} onClick={() => { setRemoveItemId(item.id); setOpenConfirmDialog(true) }}> {t`delete`}</DeleteButton>
                      )}
                    </ActionsCell>
                  </TableCellStyled>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainerStyled>
        <MekomeTablePagination
          component="div"
          count={archiveTotalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value))
            setPage(0)
          }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </TableBar>
    </>
  )
}

export default observer(ArchiveList)
